import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty.js';
import { styled } from '@linaria/react';
import { AlertCard, Button, COLORS } from '@livecommerce/design-system';
import { deleteSnackbarNotification as deleteSnackbarNotificationAction } from 'actions/notifications.js';
import useAutoHide from 'components/NotificationFooter/useAutoHide.js';

const PositionContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 2;
  width: 376px;
`;

const TYPES_MAP = {
  error: 'danger',
  warning: 'warning',
  default: 'feedback',
};

const ProgressContainer = styled.div`
  margin-top: 12px;
  width: 100%;
  height: ${(props) => props.height}px;
  position: relative;
  background: ${COLORS.light_200};
  border-radius: 4px;
  transition: height 300ms ease;
`;

const ProgressBar = styled.div`
  height: 100%;
  width: ${(props) => props.width};
  background-color: ${COLORS.green_400};
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px;
  transition: width 300ms ease;
`;

const getActions = ({ action, variant, handleClick, actionText }) => {
  if (!actionText) return null;

  const buttonTheme = variant === 'feedback' || variant === 'danger' ? 'dark' : 'light';

  return (
    <Button theme={buttonTheme} variant="solid" onClick={handleClick} disabled={action === null}>
      {actionText}
    </Button>
  );
};

const NotificationSnackbar = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.notifications.snackbar || {});
  const deleteSnackbarNotification = useCallback(() => dispatch(deleteSnackbarNotificationAction()), [dispatch]);

  const {
    title,
    text,
    ctaText,
    ctaAction = null,
    dataTestId,
    progress = null,
    type = 'default',
    autoHide = true,
    delay = 3000,
  } = data;

  const onAutoHide = useCallback(() => {
    deleteSnackbarNotification();
  }, [deleteSnackbarNotification]);

  const { autoHideTimer, delayedMessageTimer, showMessage } = useAutoHide(
    {
      autoHide,
      action: ctaAction,
      type,
      delayedMessage: false,
      message: text,
      delay,
    },
    onAutoHide,
  );

  const variant = TYPES_MAP[type] || 'feedback';

  if (isEmpty(data)) return null;

  const handleAction = () => {
    ctaAction();
    deleteSnackbarNotification();
    if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
    if (delayedMessageTimer.current) clearTimeout(delayedMessageTimer.current);
  };

  const actions = getActions({
    action: ctaAction,
    variant,
    handleClick: handleAction,
    actionText: ctaText,
  });

  return (
    <PositionContainer>
      <AlertCard
        variant={variant}
        title={title}
        isCloseButtonShown
        actions={actions}
        dataTestId={dataTestId}
        onClose={deleteSnackbarNotification}
      >
        {showMessage && text}
        {progress && (
          <ProgressContainer height={progress ? 8 : 0}>
            <ProgressBar width={`${progress}%`} />
          </ProgressContainer>
        )}
      </AlertCard>
    </PositionContainer>
  );
};
export default NotificationSnackbar;
